// Comparación de N corridas contra el análisis humano — lógica PURA (comparacion.test.ts).
// El backend calcula las métricas oficiales y la lectura con IA; acá solo se arma lo que
// se necesita ANTES de lanzar (intersección, coincidencia en vivo) y cómo se muestra.

import { accionDe } from './metricas';
import type { Accion, Caso, CeldaMatriz, ComparacionIA, LecturaIACaso, MetricaRun, Referencia, Resultado, SalidaIA } from './tipos';

export const ACCIONES: readonly Accion[] = ['aceptar', 'regenerar', 'pedir_fotos'];
export const COLUMNAS_MATRIZ: readonly CeldaMatriz[] = ['aceptar', 'regenerar', 'pedir_fotos', 'fallida'];

/** Casos de la PRIMERA corrida que están en todas las demás, en su orden (las tarjetas salen de ella). */
export function interseccionDeCasos(detalles: Array<{ casos: Caso[] }>): Caso[] {
  const [primero, ...resto] = detalles;
  if (!primero) return [];
  const sets = resto.map((d) => new Set(d.casos.map((c) => c.imagen_id)));
  return primero.casos.filter((c) => sets.every((s) => s.has(c.imagen_id)));
}

export interface Coincidencia {
  /** Resultados cuya imagen tiene referencia humana. */
  conReferencia: number;
  /** De esos, los analizados (sin `error_analisis`). */
  analizadas: number;
  /** De los analizados, los que decidieron la misma acción que la referencia. */
  coinciden: number;
  /** 0-100 sobre `analizadas`; null si no hay. */
  pct: number | null;
}

/** "¿La corrida dijo lo mismo que yo?" en vivo, con las referencias cargadas (sin esperar a la IA). */
export function coincidenciaConReferencia(resultados: Resultado[], referencias: Map<string, Referencia>): Coincidencia {
  const conRef = resultados.filter((r) => referencias.has(r.imagen_id));
  const analizadas = conRef.filter((r) => r.error_analisis === null);
  const coinciden = analizadas.filter((r) => accionDe(r) === referencias.get(r.imagen_id)!.accion).length;
  return {
    conReferencia: conRef.length,
    analizadas: analizadas.length,
    coinciden,
    pct: analizadas.length > 0 ? (coinciden / analizadas.length) * 100 : null,
  };
}

export function textoCoincidencia(c: Coincidencia): string {
  if (c.pct === null) return '—';
  return `${c.coinciden} / ${c.analizadas} (${Math.round(c.pct)}%)`;
}

export interface FilaMatriz {
  humano: Accion;
  total: number;
  celdas: Array<{ modelo: CeldaMatriz; n: number; acierto: boolean }>;
}

/** La matriz humano × modelo del backend en filas de orden fijo, con 0 donde falte una clave. */
export function filasMatriz(matriz: MetricaRun['matriz']): FilaMatriz[] {
  return ACCIONES.map((humano) => {
    const fila = (matriz?.[humano] ?? {}) as Partial<Record<CeldaMatriz, number>>;
    const celdas = COLUMNAS_MATRIZ.map((modelo) => ({ modelo, n: fila[modelo] ?? 0, acierto: modelo === humano }));
    return { humano, total: celdas.reduce((acc, c) => acc + c.n, 0), celdas };
  });
}

/** Lookup imagen → corrida → lectura de la IA (omitidos / de más / comentario). */
export function indicePorCaso(ia: SalidaIA | null | undefined): Map<string, Map<string, LecturaIACaso>> {
  const idx = new Map<string, Map<string, LecturaIACaso>>();
  for (const caso of ia?.por_caso ?? []) {
    idx.set(caso.imagen_id, new Map(caso.por_corrida.map((l) => [l.run_id, l])));
  }
  return idx;
}

/** Mismo conjunto de run_ids sin importar el orden (para encontrar comparaciones anteriores). */
export function mismosRunIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((x, i) => x === sb[i]);
}

/** Terminada = `finished_at` seteado (puede traer `error` y aun así métricas). */
export function comparacionTerminada(c: ComparacionIA): boolean {
  return c.finished_at !== null;
}
